<template>
  <div class="app-input-root">
    <label v-if="label" class="app-input-label" :for="inputId">{{ label }}</label>
    <div class="app-input-group" :class="inputGroupClasses">
      <span v-if="prependIcon" class="material-icons app-input-addon app-input-addon--prepend">{{ prependIcon }}</span>
      <input
        :id="inputId"
        class="app-input"
        :class="inputClasses"
        :value="currentValue"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-bind="$attrs"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
      />
      <span
        v-if="clearable && currentValue"
        class="material-icons app-input-addon app-input-addon--append app-input-addon--clickable"
        @click="clearValue"
      >close</span>
      <span v-else-if="appendIcon" class="material-icons app-input-addon app-input-addon--append">{{ appendIcon }}</span>
    </div>
    <small v-if="errorMessage" class="app-input-error" :class="{ 'app-input-error--visible': errorMessage }">{{ errorMessage }}</small>
    <slot name="help" />
  </div>
</template>

<script>
export default {
  name: 'AppInput',
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Number], default: undefined },
    value: { type: [String, Number], default: undefined },
    label: { type: String, default: '' },
    inputId: { type: String, default: () => 'app-input-' + Math.random().toString(36).substr(2, 9) },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    size: { type: String, default: null },
    state: { type: Boolean, default: null },
    errorMessage: { type: String, default: '' },
    prependIcon: { type: String, default: '' },
    appendIcon: { type: String, default: '' },
    clearable: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'input', 'change', 'blur', 'focus'],
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    inputGroupClasses() {
      return {
        'app-input-group--has-prepend': !!this.prependIcon,
        'app-input-group--has-append': !!(this.appendIcon || (this.clearable && this.currentValue))
      }
    },
    inputClasses() {
      return {
        'app-input--sm': this.size === 'sm',
        'app-input--lg': this.size === 'lg',
        'app-input--valid': this.state === true,
        'app-input--invalid': this.state === false || !!this.errorMessage,
        'app-input--no-radius-left': !!this.prependIcon,
        'app-input--no-radius-right': !!(this.appendIcon || (this.clearable && this.currentValue))
      }
    }
  },
  methods: {
    onInput(event) {
      const val = event.target.value
      this.$emit('input', val)
      this.$emit('update:modelValue', val)
    },
    onChange(event) { this.$emit('change', event) },
    onBlur(event) { this.$emit('blur', event) },
    onFocus(event) { this.$emit('focus', event) },
    clearValue() {
      this.$emit('input', '')
      this.$emit('update:modelValue', '')
    }
  }
}
</script>

<style scoped>
.app-input-root { width: 100%; }
.app-input-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #939393;
}
.app-input-group { display: flex; align-items: stretch; }
.app-input {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.app-input:focus { border-color: #80bdff; outline: 0; box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.15); }
.app-input:disabled { background: #e9ecef; opacity: 1; }
.app-input--sm  { padding: 0.25rem 0.5rem; font-size: 0.875rem; }
.app-input--lg  { padding: 0.5rem 1rem; font-size: 1.25rem; }
.app-input--valid  { border-color: #34C38F; }
.app-input--invalid { border-color: #F46A6A; }
.app-input--invalid:focus { box-shadow: 0 0 0 0.2rem rgba(244,106,106,0.2); }
.app-input--no-radius-left  { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.app-input--no-radius-right { border-top-right-radius: 0; border-bottom-right-radius: 0; }

.app-input-addon {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  font-size: 18px;
  color: var(--secondary, #3DF9DE);
  background: #f8f9fa;
  border: 1px solid #ced4da;
}
.app-input-addon--prepend { border-right: 0; border-radius: 0.25rem 0 0 0.25rem; }
.app-input-addon--append  { border-left: 0; border-radius: 0 0.25rem 0.25rem 0; }
.app-input-addon--clickable { cursor: pointer; }

.app-input-error { display: none; margin-top: 4px; font-size: 80%; color: #F46A6A; }
.app-input-error--visible { display: block; }
</style>
